import AddIcon from '@mui/icons-material/Add';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { AllocationPreviewTable } from './AllocationPreviewTable';
import { ReassignmentRow } from './ReassignmentRow';
import { BATCH_LABEL_IDS, type PreviewRow, type ReassignmentRowState } from './types';
import { useStep2Configure } from './useStep2Configure';
import type { BatchAllocationDetail, ReallocationBatchKey } from '../../types/response';

interface Step2ConfigureProps {
  projectId: string;
  selectedBatchDetails: BatchAllocationDetail[];
  rowsByBatch: Partial<Record<ReallocationBatchKey, ReassignmentRowState[]>>;
  onAddRow: (batch: ReallocationBatchKey) => void;
  onUpdateRow: (
    batch: ReallocationBatchKey,
    rowId: string,
    patch: Partial<ReassignmentRowState>
  ) => void;
  onRemoveRow: (batch: ReallocationBatchKey, rowId: string) => void;
}

const BatchSection = ({
  projectId,
  batch,
  rows,
  previewRows,
  hasEmptyRow,
  onAddRow,
  onUpdateRow,
  onRemoveRow,
}: {
  projectId: string;
  batch: BatchAllocationDetail;
  rows: ReassignmentRowState[];
  previewRows: PreviewRow[];
  hasEmptyRow: boolean;
  onAddRow: (batch: ReallocationBatchKey) => void;
  onUpdateRow: (
    batch: ReallocationBatchKey,
    rowId: string,
    patch: Partial<ReassignmentRowState>
  ) => void;
  onRemoveRow: (batch: ReallocationBatchKey, rowId: string) => void;
}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      <Box>
        <Typography variant="h6">{formatMessage({ id: BATCH_LABEL_IDS[batch.batch] })}</Typography>
        <Typography variant="body2" color="text.secondary">
          {formatMessage(
            { id: 'adminProjects.reallocation.configure.eligible' },
            { count: batch.pendingCount.toLocaleString() }
          )}
        </Typography>
      </Box>

      <Stack spacing={2}>
        {rows.map((row) => (
          <ReassignmentRow
            key={row.id}
            projectId={projectId}
            batch={batch.batch}
            row={row}
            rows={rows}
            perAnnotator={batch.perAnnotator}
            onChange={(patch) => onUpdateRow(batch.batch, row.id, patch)}
            onRemove={() => onRemoveRow(batch.batch, row.id)}
          />
        ))}
      </Stack>

      <Box>
        <Button
          id={`add-reassignment-row-button-${batch.batch}`}
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={hasEmptyRow}
          onClickHandler={() => onAddRow(batch.batch)}
          displayLabel={formatMessage({
            id: 'adminProjects.reallocation.configure.addReassignment',
          })}
          sx={{
            fontSize: '14px',
            textTransform: 'none',
            color: theme.palette.primary.main,
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'adminProjects.reallocation.configure.previewLabel' })}
        </Typography>
        <AllocationPreviewTable rows={previewRows} />
        <Typography variant="caption" color="text.secondary">
          {formatMessage({ id: 'adminProjects.reallocation.configure.aiFootnote' })}
        </Typography>
      </Box>
    </Box>
  );
};

export const Step2Configure = ({
  projectId,
  selectedBatchDetails,
  rowsByBatch,
  onAddRow,
  onUpdateRow,
  onRemoveRow,
}: Step2ConfigureProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const batchSections = useStep2Configure(selectedBatchDetails, rowsByBatch);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {formatMessage({ id: 'adminProjects.reallocation.configure.heading' })}
      </Typography>
      {batchSections.map(({ batch, rows, previewRows, hasEmptyRow }) => (
        <BatchSection
          key={batch.batch}
          projectId={projectId}
          batch={batch}
          rows={rows}
          previewRows={previewRows}
          hasEmptyRow={hasEmptyRow}
          onAddRow={onAddRow}
          onUpdateRow={onUpdateRow}
          onRemoveRow={onRemoveRow}
        />
      ))}
    </Box>
  );
};
